/**
 * leadHandoff.ts — read the widget's opaque leadId out of the URL, and get it
 * back out of the address bar.
 *
 * WHY fragment, not query: `POST /v1/clinic/leads/resume` returns H/W + email
 * and is replayable with no TTL. Putting leadId in `?lead=` would write it into
 * load-balancer access logs on the first request. A fragment is never sent to
 * the server (same posture as the pharmacy link token in PharmacyAuthPage).
 *
 * THE QUERY FORM IS DEPRECATED BUT STILL READ, and — since 2026-08-28 — also
 * SCRUBBED. `?lead=<uuid>` is the older widget hand-off, kept working for embeds
 * cached on marketing pages we do not redeploy. It was being read and then left
 * sitting in the address bar for the rest of the journey, which is the same hole
 * `?resume=` had and was fixed for (see resumeHandoff.ts): a query param rides
 * GTM's `page_location` into a container administered outside this estate, the
 * `Referer` header on the next hop, and the browser's own history. And a leadId
 * is worse than an ordinary id — it is a bearer capability that dereferences to
 * a named person's height, weight and email address.
 *
 * Be honest about what the scrub can and cannot do, exactly as resumeHandoff is:
 * by the time this runs, the request that CARRIED `?lead=` here has already been
 * logged by the load balancer. What the scrub stops is the id travelling any
 * further — into `page_location`, into the referrer, into history, and into
 * every in-app navigation after it. Every link the WIDGET mints today already
 * uses the fragment on the assessment redirect (`embed/main.ts`); this fallback
 * retires when the cached embeds have aged out.
 *
 * WHAT THE SCRUB COSTS, so nobody rediscovers it as a bug: on the legacy
 * `?lead=`-only lane (no `?assessment=`), the address bar was the only durable
 * record of who the visitor was, so a refresh at the projection used to resume
 * them. Scrubbed, a refresh there is an ordinary DIRECT ENTRY and lands on
 * Step 0. Nothing is lost but the in-tab position — the Lead still exists, and
 * the canonical lane is unaffected because its durability comes from
 * `?assessment=`, which stays. It is the same trade the resume scrub made, for
 * the same reason, and the id is more sensitive here than there.
 *
 * Callers must capture the id BEFORE the scrub runs — JourneyPage and
 * CallbackPage both freeze it in lazy state at mount. After the scrub there is
 * nothing left to read.
 */

import type { Location } from 'react-router-dom';
import { stripKeyFromLocation } from './urlKeys';

/** The one key name, so the read and the scrub cannot drift apart. */
const LEAD_KEY = 'lead';

/** Accept `#lead=<uuid>` only — the canonical form the widget emits. */
export function readLeadIdFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const named = params.get(LEAD_KEY)?.trim();
  return named || null;
}

/** DEPRECATED `?lead=<uuid>` — cached embeds only. */
export function readLegacyLeadIdFromQuery(search: string): string | null {
  return new URLSearchParams(search).get(LEAD_KEY)?.trim() || null;
}

/** Either form, fragment winning — the one read the journey should use. */
export function readLeadId(location: Pick<Location, 'search' | 'hash'>): string | null {
  return readLeadIdFromHash(location.hash) ?? readLegacyLeadIdFromQuery(location.search);
}

/**
 * The hand-off id carried in history STATE rather than on the URL.
 *
 * WHY THIS EXISTS. The callback lane (`CallbackPage`) is entered directly from
 * the widget with the id on the URL, and its in-app Back replaces onto the
 * clinic journey — which needs the id, or a visitor who backs out of the
 * callback form has to retype the height and weight the widget already
 * collected. Once the URL is scrubbed there is nothing left on it to carry, so
 * the id travels in `navigate(..., { state })`: an in-memory property of the
 * history entry that is never sent to a server, never appears in the address
 * bar, and never reaches GTM's `page_location`. It is the same place the Tier-2
 * re-entry token is kept, for the same reason.
 *
 * TRUST MODEL, unchanged: history state is script-writable by anything already
 * running on this origin, so a `leadId` read from it is exactly as trustworthy
 * as one read from `?lead=` — possession of an unguessable id, which is what
 * this lane has always been authenticated by. Anything able to forge the state
 * could equally navigate to `?lead=`. (Contrast `journeyReturnTo`, which was a
 * spoofable *destination* and is deliberately ignored — see
 * `callbackHistory.spec.tsx`. A destination is an open redirect; an id is not.)
 */
export function readLeadIdFromNavigationState(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const value = (state as { leadId?: unknown }).leadId;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * The search + hash this location would have with every `lead` key removed.
 *
 * PURE, and the only thing this module exposes about removal — the NAVIGATION
 * lives in `handoffScrub.ts`, which owns both hand-off keys and rewrites the
 * address bar exactly once. There are two ids to scrub off one address bar, and
 * a `navigate` inside an effect does not refresh `useLocation()` before the next
 * statement runs, so two independent scrubs each computed from the same
 * `location` snapshot make the second `replace` write back what the first
 * removed. Keeping the navigation out of here is what makes that unbuildable.
 *
 * Every OTHER key survives, byte for byte: `?assessment=` is the journey's
 * durable identity, the marketing tags (`channel`/`campaign`/`v`) are Salman's
 * attribution, and a plain `#faq` anchor is somebody's link — see
 * `urlKeys.ts` for why removal is a segment filter rather than a
 * `URLSearchParams` round-trip.
 */
export function stripLeadKeys(location: Pick<Location, 'search' | 'hash'>): {
  search: string;
  hash: string;
} {
  return stripKeyFromLocation(location, LEAD_KEY);
}
